import { NgIf } from '@angular/common';
import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  L10nTranslationModule,
  L10nTranslationService,
  L10N_LOCALE,
  L10nLocale,
} from 'angular-l10n';
import { MenuItem } from 'primeng/api';
import { StepsModule } from 'primeng/steps';
import { ButtonModule } from 'primeng/button';
import { AppConstants } from '../../../app.constants';
import {
  IsSupplierContactFormValid,
  SupplierContactFormModel,
} from '../../../core/models/supplierDB/supplierContactForm.model';
import { SupplierCreateRequestModel } from '../../../core/models/supplierDB/supplierCreateRequest.model';
import {
  SupplierSelectionFormModel,
  IsSupplierSelectionFormValid,
  GetEmptySupplierSelectionForm,
} from '../../../core/models/supplierDB/supplierSelectionForm.model';
import { IndustriesService } from '../../../core/services/enum/industries.service';
import { RegionsService } from '../../../core/services/enum/regions.service';
import { SegmentsService } from '../../../core/services/enum/segments.service';
import { TransportServicesService } from '../../../core/services/enum/transportServices.service';
import { HttpSupplierDbService } from '../../../core/services/http/httpSupplierDB.service';
import { LanguageService } from '../../../core/services/other/language.service';
import { RegistrationContactComponent } from '../registration-contact/registration-contact.component';
import { RegistrationInfoComponent } from '../registration-info/registration-info.component';
import { RegistrationOverviewComponent } from '../registration-overview/registration-overview.component';
import { RegistrationSelectionComponent } from '../registration-selection/registration-selection.component';
import { CardModule } from 'primeng/card';
import { HttpFileService } from '../../../core/services/http/httpFile.service';
import { TooltipModule } from 'primeng/tooltip';
import { MaintenanceModeBannerComponent } from "src/app/core/components/maintenance-mode-banner/maintenance-mode-banner.component";

@Component({
  selector: 'supplierDB-registration-registration-page',
  standalone: true,
  imports: [
    RegistrationContactComponent,
    RegistrationInfoComponent,
    RegistrationOverviewComponent,
    RegistrationSelectionComponent,
    StepsModule,
    ButtonModule,
    CardModule,
    L10nTranslationModule,
    NgIf,
    TooltipModule,
    MaintenanceModeBannerComponent
  ],
  providers: [
    IndustriesService,
    RegionsService,
    SegmentsService,
    TransportServicesService,
    HttpSupplierDbService,
    HttpFileService,
  ],
  templateUrl: './registration-page.component.html',
  styleUrl: './registration-page.component.scss',
})
export class RegistrationPageComponent implements OnInit {
  // steps menu items
  public stepsItems: MenuItem[] = [];
  public activeStep: number = 0;
  public lastStep: number = 3;

  public contactData!: SupplierContactFormModel | null;
  public selectionData!: SupplierSelectionFormModel | null;

  public contactDataValid: boolean = false;
  public selectionDataValid: boolean = false;

  @ViewChild(RegistrationContactComponent)
  contactFormComponent!: RegistrationContactComponent;
  @ViewChild(RegistrationSelectionComponent)
  selectionFormComponent!: RegistrationSelectionComponent;

  constructor(
    private _translationService: L10nTranslationService,
    private _supplierDBService: HttpSupplierDbService,
    private _languageService: LanguageService,
    private _fileService: HttpFileService,
    private _router: Router,
    @Inject(L10N_LOCALE) public locale: L10nLocale
  ) {
    this._languageService.languageLocale$.asObservable().subscribe(() => {
      this._initStepsMenuItems();
    });
  }

  ngOnInit() {
    this.contactData = null;
    this.selectionData = null;

    this.contactDataValid = false;
    this.selectionDataValid = false;

    this.getContactData();
    this.getSelectionData();
  }

  /**
   * Initializes the steps menu items for the registration process
   * @returns {void}
   */
  private _initStepsMenuItems(): void {
    this.stepsItems = [
      {
        label: this._translationService.translate('CARRIERDB.INFORMATION'),
      },
      {
        label: this._translationService.translate('CARRIERDB.COMPANY_CONTACT'),
      },
      {
        label: this._translationService.translate('CARRIERDB.COMPANY_DETAILS'),
        disabled: !this.contactDataValid,
      },
      {
        label: this._translationService.translate('CARRIERDB.OVERVIEW'),
        disabled: !this.contactDataValid || !this.selectionDataValid,
      },
    ];
  }

  /**
   * Loads the logo from local storage and converts it into a File object.
   * If no logo is found in local storage, it returns null.
   * @returns {File | null} - The loaded logo as a File object or null if no logo is found.
   */
  private _loadLogo(): File | null {
    const base64Data = localStorage.getItem(
      AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_LOGO
    );

    if (base64Data == null) return null;
    var base64 = base64Data.split(',');
    const byteCharacters = atob(base64[1]);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const type = this._fileService.getImageType(base64Data);

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/' + type });
    return new File([blob], 'image.' + type, { type: 'image/' + type });
  }

  private _loadLogoType(): string | null {
    const base64Data = localStorage.getItem(
      AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_LOGO
    );
    if (base64Data == null) return null;

    return this._fileService.getImageType(base64Data);
  }

  /**
   * Checks if the selection data is valid
   * @returns {boolean}
   */
  private _isSelectionDataValid(): boolean {
    if (this.selectionData === null) {
      return false;
    } else {
      return IsSupplierSelectionFormValid(this.selectionData);
    }
  }

  /**
   * Checks if the contact data is valid
   * @returns {boolean}
   */
  private _isContactDataValid(): boolean {
    if (this.contactData === null) {
      return false;
    } else {
      return IsSupplierContactFormValid(this.contactData);
    }
  }

  /**
   * Sets the current step number
   * @param {number} event - The step number to navigate to
   */
  public onStepChange(event: number): void {
    this.activeStep = event;
  }

  /**
   * Goes to the previous step in the registration process
   */
  public onNextStep() {
    if (this.activeStep < this.lastStep) {
      this.activeStep++;
    }
  }

  /**
   * Goes to the previous step in the registration process
   */
  public onPreviousStep() {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  }

  /**
   * Retrieves the supplier contact data from local storage and updates the component's properties.
   * If selection data is found, its parsed from JSON and checked for validity
   * @returns {void}
   */
  public getContactData(): void {
    var contactDataString = localStorage.getItem(
      AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_CONTACT
    );
    if (contactDataString !== null) {
      this.contactData = JSON.parse(contactDataString);
      this.contactDataValid = this._isContactDataValid();
      this._initStepsMenuItems();
    }
  }

  /**
   * Retrieves the supplier selection data from local storage and updates the component's properties.
   * If selection data is found, its parsed from JSON and checked for validity
   * @returns {void}
   */
  public getSelectionData(): void {
    var selectionDataString = localStorage.getItem(
      AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_SELECTION
    );

    // Check if selection data is present in local storage
    if (selectionDataString !== null) {
      this.selectionData = JSON.parse(selectionDataString);

      // Validate selection data and update validity flag
      this.selectionDataValid = this._isSelectionDataValid();
    } else {
      this.selectionData = GetEmptySupplierSelectionForm();
      this.selectionDataValid = false;
    }
    this._initStepsMenuItems();
  }


  /**
   * Submits the registration form by creating a new supplier in the database.
   * It retrieves contact and selection data from local storage, constructs a
   * {@link SupplierCreateRequestModel} object, and sends it to the server.
   * After successful registration, it clears the local storage and navigates to the
   * thank you page.
   * @returns {void}
   */
  public onSubmit(): void {
    this.getContactData();
    this.getSelectionData();

    var supplierData = new SupplierCreateRequestModel();
    supplierData.setContactValues(this.contactData!);
    const type = this._loadLogoType();
    const logo = this._loadLogo();
    if (logo !== null && type !== null) {
      supplierData.logoUploaded = true;
      supplierData.uploadedLogoType = type;
    }

    if (this.selectionData !== null) {
      supplierData.setSelectionValues(this.selectionData!);
    } else {
      supplierData.setSelectionValues(GetEmptySupplierSelectionForm());
    }
    this._supplierDBService
      .registerNewSupplier(supplierData)
      .subscribe((id) => {
        if (id !== null) {
          localStorage.setItem(AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_JUST_REGISTERED, "true");
          if (logo !== null && type !== null) {
            this._fileService
              .uploadSupplierLogo(logo, type, id)
              .subscribe((result) => {
                if (result.uploaded.length > 0) {
                  localStorage.removeItem(
                    AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_LOGO
                  );
                  localStorage.removeItem(
                    AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_CONTACT
                  );
                  localStorage.removeItem(
                    AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_SELECTION
                  );
                  this._router.navigate(['/thankYou']);
                }
              });
          } else {
            localStorage.removeItem(
              AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_CONTACT
            );
            localStorage.removeItem(
              AppConstants.SDB_SUPPLIER_LOCAL_STORAGE_SELECTION
            );
            this._router.navigate(['/thankYou']);
          }
        }
      });
  }
}
